import { BackendServerAvailability } from "./BackendServerAvailability";
import { AuthState } from "./AuthState";
import {
  navigateToLocationBeforeLogin,
  writeCurrentLocationToLocalStorage,
} from "./LocationRestorerAfterLogin";
import Auth from "../auth/Auth";
import { AuthAlwaysAuthorizedStub } from "../auth/AuthAlwaysAuthorizedStub";
import { BackendConfig } from "../Config";
import { SongsAvailability } from "./SongsAvailability";
import { PlaylistPusher } from "../playlist/sync/PlaylistPusher";
import { PlaylistFetcher } from "../playlist/sync/PlaylistFetcher";
import { Client, RequestPolicy } from "urql";
import SwitchUtils from "../common/SwitchUtils";
import { LoginByDifferentUserHandler } from "../auth/LoginByDifferentUserHandler";
import { SONGSHIP_STUB_TOKEN } from "../auth/stub/StubLogin";
import { RoutePaths } from "../RoutePaths";
import { NavigateFunction } from "react-router-dom";
import { PlaylistV3ToV4 } from "../dto/migration/PlaylistV3ToV4";
import { fetchSongsIds, fetchSongsDelta, fetchSongsFull } from "./SongsFetcher";
import { SongUb } from "../gql/graphql";
import { redirectToLandingIfBackingOutOfLogin } from "../auth/Auth0RedirectGuard";
import { startBackgroundJobPersistChordsheetsOfSongs } from "../chordsheet/BackgroundJobPersistChordsheetsOfSongs";
import { LocalStorageKey } from "../localStoragePersistence/common/LocalStorageKey";

export interface AppInitializationResult {
  readonly backendServerAvailability: BackendServerAvailability;
  readonly authState: AuthState;
  readonly songsAvailability: SongsAvailability;
  readonly songs: readonly SongUb[] | undefined;
}

export const songsLastUpdatedAtKey = new LocalStorageKey(
  "App",
  "songsLastUpdatedAt",
);

export function initApp(
  auth: Auth,
  graphQlClient: Client,
  navigate: NavigateFunction,
  cacheHydratedPromise: Promise<void>,
): Promise<AppInitializationResult> {
  console.info("Starting initialization");

  //Note, if you impelement your migration: For migrations you cannot expect a ToC to be available.
  PlaylistV3ToV4.migrate();
  console.debug("All migrations done");

  const appInitializationResult = {
    backendServerAvailability: BackendServerAvailability.Undecided,
    authState: AuthState.Undecided,
    songsAvailability: SongsAvailability.ToCNotAvailable,
    songs: undefined,
  };

  return checkBackendConnection()
    .then((b: BackendServerAvailability) =>
      updateProp(appInitializationResult, "backendServerAvailability", b),
    )
    .then((r: AppInitializationResult) =>
      verifyAuthentication(r.backendServerAvailability, auth, navigate).then(
        (a) => updateProp(r, "authState", a),
      ),
    )
    .then((r: AppInitializationResult) =>
      updateProp(
        r,
        "authState",
        checkIfDifferentUserLoggedInLastTime(r.authState, auth),
      ),
    )
    .then((r: AppInitializationResult) => {
      switch (r.authState) {
        case AuthState.RedirectToLoginNecessary:
          return redirectToLogin(auth, navigate).then((t) =>
            updateProp(r, "songsAvailability", t),
          );
        case AuthState.OkWithOnlineAuthentication:
          return cacheHydratedPromise
            .then(() =>
              Promise.all([
                syncPlaylistsIfOnlineAndAuthenticated(graphQlClient, auth),
                fetchSongsOnlineAndUpdateResult(r, graphQlClient),
              ]).then((resultArray) => {
                //Promise.all resolves with an array of values, where each value's index in the array is the same as the index of the Promise in the original array passed to Promise.all that generated that value.
                //https://stackoverflow.com/a/41960710
                return resultArray[1];
              }),
            )
            .catch((error) => {
              console.error(
                "Unable to fetch songs while online. App will continue without songs.",
                error,
              );
              return updateProp(
                r,
                "songsAvailability",
                SongsAvailability.ToCNotAvailable,
              );
            });
        case AuthState.OkButOffline: {
          //We wait for the cacheHydratedPromise to ensure the IndexedDB is fully loaded before querying.
          return cacheHydratedPromise
            .then(() =>
              fetchSongsAndUpdateResult(r, graphQlClient, "cache-only"),
            )
            .catch((error) => {
              console.error(
                "Unable to fetch songs from cache while offline. App will continue without songs.",
                error,
              );
              return updateProp(
                r,
                "songsAvailability",
                SongsAvailability.ToCNotAvailable,
              );
            });
        }
        case AuthState.LocalStorageDeletionNecessary:
        case AuthState.Failed:
        case AuthState.Undecided:
        case AuthState.SessionExpired:
          return updateProp(
            r,
            "songsAvailability",
            SongsAvailability.ToCNotAvailable,
          );
        default:
          return SwitchUtils.throwUnsupportedValue(r.authState);
      }
    });
}

function checkBackendConnection(): Promise<BackendServerAvailability> {
  console.debug("Checking backend connection to: " + BackendConfig.baseUrl);
  return fetch(BackendConfig.baseUrl, { mode: "cors" })
    .then((response) => {
      if (response.ok) {
        console.debug(
          "Backend server availability: ",
          BackendServerAvailability.Available,
        );
        return BackendServerAvailability.Available;
      } else {
        console.warn(
          "AppInitializer: check backend connection did not answer with ok but " +
            response.status +
            ". Strange... Backend state will be unavailable.",
        );
        return BackendServerAvailability.Unavailable;
      }
    })
    .catch((e) => {
      console.debug(
        "Backend server availability will be unavailable. Reason: ",
        e,
      );
      return BackendServerAvailability.Unavailable;
    });
}

function verifyAuthentication(
  backendServerAvailability: BackendServerAvailability,
  auth: Auth,
  navigate: NavigateFunction,
): Promise<AuthState> {
  if (backendServerAvailability === BackendServerAvailability.Unavailable) {
    return Promise.resolve(AuthState.OkButOffline);
  }
  if (backendServerAvailability === BackendServerAvailability.Undecided) {
    return Promise.reject(
      `BackendServerAvailability is ${BackendServerAvailability[backendServerAvailability]}. This should not happen here.`,
    );
  }

  // BackendServerAvailability.Available
  if (auth.isAuthenticated()) {
    verifyNoOneIsCallingCallbackRouteWhenAlreadyAuthenticated(navigate);
    return Promise.resolve(AuthState.OkWithOnlineAuthentication);
  }
  if (window.location.href.includes(RoutePaths.CALLBACK)) {
    return handleCallback(auth, navigate);
  }
  if (auth.canAttemptSessionRenewal()) {
    return attemptSessionRenewal(auth);
  }
  return Promise.resolve(AuthState.RedirectToLoginNecessary);
}

function attemptSessionRenewal(auth: Auth): Promise<AuthState> {
  return new Promise<AuthState>((resolve) => {
    auth.renewSession((renewResult) => {
      if (renewResult === AuthState.OkWithOnlineAuthentication) {
        resolve(AuthState.OkWithOnlineAuthentication);
      } else if (renewResult === AuthState.SessionExpired) {
        resolve(AuthState.RedirectToLoginNecessary);
      } else {
        // Any other error (e.g., AuthState.Failed due to network timeout or downtime)
        // should fallback to offline mode rather than forcing a login redirect.
        console.warn(
          "Session renewal failed with non-expiration error. Falling back to offline mode.",
          renewResult,
        );
        resolve(AuthState.OkButOffline);
      }
    });
  });
}

function verifyNoOneIsCallingCallbackRouteWhenAlreadyAuthenticated(
  navigate: NavigateFunction,
) {
  if (window.location.href.includes(RoutePaths.CALLBACK)) {
    console.warn(
      "Callback route was called. But the user already is authenticated!",
    );
    navigate("/");
  }
}

function isRootPath(): boolean {
  const path = window.location.pathname;
  return path === "/" || path === "" || path === "/index.html";
}

function hasForceSignupQueryParam(): boolean {
  return new URLSearchParams(window.location.search).has(
    RoutePaths.FORCE_SIGNUP_QUERY_PARAM,
  );
}

function hasForceLoginQueryParam(): boolean {
  return (
    new URLSearchParams(window.location.search).has(
      RoutePaths.FORCE_LOGIN_QUERY_PARAM,
    ) || hasForceSignupQueryParam()
  );
}

function redirectToLogin(
  auth: Auth,
  navigate: NavigateFunction,
): Promise<SongsAvailability> {
  // Issue #322: Unauthenticated users that visit the root of the application should see the
  // feature overview / landing page instead of being redirected straight to the Auth0 login.
  // Deep links (any non-root path) continue to redirect to the login so the original URL is
  // preserved via writeCurrentLocationToLocalStorage(). The landing page itself contains a
  // "Login" button that links back to "/?login=true" to explicitly trigger the login flow, and a
  // "Register" button that links to "/?signup=true" to open the sign-up screen of Auth0.
  // The landing page is only shown for real (Auth0) authentication. With the stub auth used in
  // Cypress tests, redirect immediately to the stub login screen instead.
  if (
    isRootPath() &&
    !hasForceLoginQueryParam() &&
    !(auth instanceof AuthAlwaysAuthorizedStub)
  ) {
    console.debug(
      "online but not yet authenticated --> redirect to landing page",
    );
    window.location.replace(RoutePaths.LANDING_PAGE);
    return Promise.resolve(SongsAvailability.RedirectToLoginNecessary);
  }
  // If the user pressed "Back" from the Auth0 login page without logging in, the force-login URL
  // (e.g. "/?login=true") would bounce them straight back into Auth0 (login loop). Detect that
  // back-out and send them to the landing page instead. (Firefox/Vivaldi reload on "Back"; Chrome's
  // bfcache case is handled by installAuth0LoginBackoutGuard.) Skipped for the Cypress stub auth.
  if (
    !(auth instanceof AuthAlwaysAuthorizedStub) &&
    redirectToLandingIfBackingOutOfLogin()
  ) {
    return Promise.resolve(SongsAvailability.RedirectToLoginNecessary);
  }
  console.debug("online but not yet authenticated --> redirect to login");
  writeCurrentLocationToLocalStorage();
  auth.login(navigate, hasForceSignupQueryParam() ? "signup" : "login");
  return Promise.resolve(SongsAvailability.RedirectToLoginNecessary);
}

function handleCallback(
  auth: Auth,
  navigate: NavigateFunction,
): Promise<AuthState> {
  const locHash = window.location!.hash;
  if (
    new RegExp("access_token|id_token|error|" + SONGSHIP_STUB_TOKEN).test(
      locHash,
    )
  ) {
    console.debug("auth calls back with the following hash: ", {
      hash: window.location!.hash,
    });
    return auth.handleAuthentication().then((r) => {
      navigateToLocationBeforeLogin("/", navigate);
      return Promise.resolve(r);
    });
  } else {
    return Promise.reject(
      "The callback url '" + locHash + "' is not a proper callback url.",
    );
  }
}

function checkIfDifferentUserLoggedInLastTime(
  r: AuthState,
  auth: Auth,
): AuthState {
  if (r === AuthState.OkWithOnlineAuthentication) {
    const auth0Subject = auth.getAuth0Subject();
    if (
      LoginByDifferentUserHandler.isMessageToUserNecessaryThatDeletionOfLocalStorageDataIsNeededToLogin(
        auth0Subject,
      )
    ) {
      return AuthState.LocalStorageDeletionNecessary;
    } else {
      LoginByDifferentUserHandler.persistCurrentAuth0Subject(auth0Subject);
      return r;
    }
  } else {
    return r;
  }
}

function fetchSongsOnlineAndUpdateResult(
  r: AppInitializationResult,
  graphQlClient: Client,
): Promise<AppInitializationResult> {
  const songsLastUpdatedAt =
    window.localStorage.getItem(songsLastUpdatedAtKey.localStorageKey) ||
    undefined;
  return fetchSongsIds(graphQlClient)
    .then(() => {
      return fetchSongsDelta(graphQlClient, songsLastUpdatedAt);
    })
    .then((deltaSongs) => {
      console.info(`Fetched ${deltaSongs.length} updated/new songs.`);

      const allSongsPromise = fetchSongsFull(graphQlClient, "cache-only").catch(
        (err) => {
          console.warn(
            "fetchSongsFull: Cache miss on full songs list. Fetching full list from network.",
            err,
          );
          return fetchSongsFull(graphQlClient, "cache-first");
        },
      );

      allSongsPromise.then((allSongs) => {
        const newWatermark = calculateNewWatermark(
          songsLastUpdatedAt,
          deltaSongs,
        );

        if (newWatermark !== undefined) {
          window.localStorage.setItem(
            songsLastUpdatedAtKey.localStorageKey,
            newWatermark,
          );
        }
        startBackgroundJobPersistChordsheetsOfSongs(allSongs, deltaSongs);
      });

      return allSongsPromise;
    })
    .then((songs) => ({
      ...r,
      songs,
      songsAvailability: SongsAvailability.ToCIsAvailable,
    }));
}

function calculateNewWatermark(
  songsLastUpdatedAt: string | undefined,
  deltaSongs: SongUb[],
): string | undefined {
  if (deltaSongs.length === 0) {
    return songsLastUpdatedAt;
  }
  const maxUpdatedAt = deltaSongs.reduce(
    (max, song) => {
      if (!song.updatedAt) return max;
      if (!max) return song.updatedAt;
      return new Date(song.updatedAt).getTime() > new Date(max).getTime()
        ? song.updatedAt
        : max;
    },
    undefined as string | undefined,
  );
  return maxUpdatedAt ?? songsLastUpdatedAt;
}

function fetchSongsAndUpdateResult(
  r: AppInitializationResult,
  graphQlClient: Client,
  requestPolicy: RequestPolicy,
): Promise<AppInitializationResult> {
  return fetchSongsFull(graphQlClient, requestPolicy).then((songs) => ({
    ...r,
    songs,
    songsAvailability: SongsAvailability.ToCIsAvailable,
  }));
}

function syncPlaylistsIfOnlineAndAuthenticated(
  graphQlClient: Client,
  auth: Auth,
): Promise<void> {
  const fetcher = new PlaylistFetcher(graphQlClient);
  const pusher = new PlaylistPusher(auth);
  return fetcher
    .run()
    .then((fetchResult) => {
      return pusher.run().then((pushResult) => ({
        fetchResult: fetchResult,
        pushResult: pushResult,
      }));
    })
    .then((syncResult) => {
      if (syncResult.pushResult.pushError.length > 0) {
        console.warn("There were sync errors!", syncResult);
      } else {
        console.info("PlaylistPusher finished.", syncResult);
      }
      return;
    })
    .catch(function (error) {
      const message = "Error while syncing playlists";
      console.error(message, error);
      return;
    });
}

function updateProp<TObj, K extends keyof TObj>(
  obj: TObj,
  key: K,
  value: TObj[K],
): TObj {
  return {
    ...obj,
    [key]: value,
  };
}
